import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { TOKEN, useURL } from "../../Config";
import { Role, IUser } from "../../../types";

interface LoginData {
  token?: string;
  user: IUser;
}

interface UserState {
  isAuth: boolean;
  role: "" | "admin";
  user: IUser | null;
  loading: boolean;
  error?: string;
}

const initialState: UserState = {
  user: null,
  isAuth: false,
  role: "",
  loading: false,
};

const UserSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    login: (state, { payload }: PayloadAction<LoginData>) => {
      if (payload.token) {
        localStorage.setItem(TOKEN, payload.token);
      }

      if (payload.user) {
        state.isAuth = true;
        state.user = payload.user;
      }
    },
    logout: (state) => {
      state.isAuth = false;
      localStorage.removeItem(TOKEN);
      localStorage.clear();
      state.loading = false;
      state.user = null;
    },
    setUser: (state, { payload }) => {
      state.user =
        payload && "user" in payload ? payload.user : (payload as IUser | null);
    },
    setRole: (state, { payload }: PayloadAction<"" | "admin">) => {
      localStorage.setItem("role", payload);
      state.role = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(loadUser.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.loading = false;
        state.isAuth = true;
        state.user = action.payload;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load user!";
      });
  },
});

export const loadUser = createAsyncThunk<IUser, void, { rejectValue: string }>(
  "user/loadUser",
  async (_, thunkAPI) => {
    const token = localStorage.getItem("AUTH_TOKEN");

    if (!token) {
      return thunkAPI.rejectWithValue("No token found!");
    }

    try {
      const res = await axios.get(`${useURL}/users/logged-in-user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const role = localStorage.getItem("role") as Role | null;
      if (role) {
        thunkAPI.dispatch(setRole(role));
      }

      return res.data;
    } catch (error: any) {
      // Clear auth state if token is invalid
      thunkAPI.dispatch(logout());
      return thunkAPI.rejectWithValue(error.message || "Error loading user!");
    }
  }
);
export const { login, logout, setUser, setRole } = UserSlice.actions;

export default UserSlice.reducer;
